/**
 * The client's consent history.
 *
 * ===========================================================================
 * A WITHDRAWAL CREATES A RECORD. IT NEVER EDITS ONE.
 * ===========================================================================
 * There is no PATCH and no DELETE anywhere on this resource — not on the API, and so
 * not on this screen. The original decision is the evidence that consent was validly
 * held for the period it covered, and an in-place edit would destroy the only answer to
 * "were we entitled to send that in March".
 *
 * So this is a HISTORY, shown oldest decision and newest side by side, rather than a
 * set of switches showing today's position. `is_current` marks which decisions are the
 * operative ones now; it is derived at read time from the rows themselves, so there is
 * no stored flag that could disagree with the record.
 * ===========================================================================
 *
 * The list is a bare array, not a paginated page — consent history is short and the
 * whole of it is the point.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type ClientConsentRecord,
  type ClientDetail,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Form,
  FormActions,
  FormCheckbox,
  FormInput,
  FormSelect,
  Modal,
  Table,
  useToast,
  useZodForm,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';
import { z } from 'zod';

import { Can } from '@/auth/permissions';
import { formatDateTime } from '@/lib/format';

const schema = z.object({
  consent_type: z.string().min(1, 'Say what is being consented to'),
  granted: z.boolean(),
  contact: z.string(),
  policy_version: z.string(),
});

type Values = z.infer<typeof schema>;

export function ClientConsentsTab({ client }: { client: ClientDetail }) {
  const [capturing, setCapturing] = useState(false);
  const [withdrawing, setWithdrawing] = useState<ClientConsentRecord | null>(null);

  const history = useApiQuery<ClientConsentRecord[]>(
    qk.sub('clients', client.id, 'consents'),
    STAFF.clientConsents(client.id),
  );

  const contactName = (id: string | null) =>
    client.contacts.find((contact) => contact.id === id)?.full_name ?? '—';

  const columns: Column<ClientConsentRecord>[] = [
    {
      key: 'consent_type',
      header: 'Consent',
      render: (row) => (
        <div className="min-w-0">
          <p className="text-ink">{row.consent_type}</p>
          {row.policy_version && (
            <p className="text-xs text-ink-muted">Policy {row.policy_version}</p>
          )}
        </div>
      ),
    },
    {
      key: 'granted',
      header: 'Decision',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={row.granted ? 'success' : 'neutral'} size="sm">
            {row.granted ? 'Given' : 'Withdrawn'}
          </Badge>
          {row.is_current && (
            <Badge tone="info" size="sm">
              Operative now
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Given by',
      hideOnMobile: true,
      render: (row) => <span className="text-ink">{contactName(row.contact)}</span>,
    },
    {
      key: 'granted_at',
      header: 'Recorded',
      hideOnMobile: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-ink">{formatDateTime(row.granted_at)}</p>
          {row.ip_address && <p className="text-xs text-ink-muted">from {row.ip_address}</p>}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) =>
        // Only a consent that is currently GIVEN can be withdrawn. Withdrawing a
        // withdrawal is refused server-side with a reason; not offering it is kinder.
        row.is_current && row.granted ? (
          <Can actions={client._actions} action="edit">
            <Button variant="ghost" size="sm" onClick={() => setWithdrawing(row)}>
              Withdraw
            </Button>
          </Can>
        ) : null,
    },
  ];

  return (
    <>
      <Card>
        <CardHeader
          title="Consent history"
          description="Every decision this client has made, in full. Nothing here is ever edited or removed — a withdrawal is recorded as a new decision."
          actions={
            <Can actions={client._actions} action="edit">
              <Button variant="secondary" onClick={() => setCapturing(true)}>
                Capture consent
              </Button>
            </Can>
          }
        />
        <CardBody>
          <Table
            caption="Consent history"
            columns={columns}
            rows={history.data ?? []}
            rowKey={(row) => row.id}
            loading={history.isLoading}
            error={history.error}
            onRetry={() => void history.refetch()}
            emptyTitle="No consent recorded"
            emptyDescription="Nothing has been captured for this client yet."
          />
        </CardBody>
      </Card>

      <CaptureDialog open={capturing} onClose={() => setCapturing(false)} client={client} />
      <WithdrawDialog
        client={client}
        consent={withdrawing}
        onClose={() => setWithdrawing(null)}
      />
    </>
  );
}

function CaptureDialog({
  open,
  onClose,
  client,
}: {
  open: boolean;
  onClose: () => void;
  client: ClientDetail;
}) {
  const api = useApi();
  const { toast } = useToast();

  const form = useZodForm(schema, {
    defaultValues: {
      consent_type: '',
      granted: true,
      contact: '',
      policy_version: '',
    },
  });

  const capture = useApiMutation<ClientConsentRecord, Values>(
    {
      mutationFn: (values) =>
        api.post<ClientConsentRecord>(`${STAFF.clientConsents(client.id)}/`, {
          consent_type: values.consent_type.trim(),
          granted: values.granted,
          // Absent, not empty: "" against a UUID field is a rejection, and it would
          // also record "no contact gave this" rather than "nobody was recorded".
          contact: values.contact || undefined,
          policy_version: values.policy_version || undefined,
        }),
      invalidates: [qk.sub('clients', client.id, 'consents')],
    },
    {
      onSuccess: () => {
        toast({ title: 'Consent recorded', tone: 'success' });
        form.reset();
        onClose();
      },
    },
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Capture a consent decision"
      description="Recorded against the policy text in force today, with the address it was captured from."
    >
      <Form
        form={form}
        ariaLabel="Capture consent"
        error={capture.error}
        onSubmit={(values) => capture.mutateAsync(values)}
      >
        <FormInput
          form={form}
          name="consent_type"
          label="What is being consented to"
          hint="The firm’s own wording, e.g. marketing_email or data_processing. It is what the record is grouped by."
          autoFocus
          autoComplete="off"
        />
        <FormSelect
          form={form}
          name="contact"
          label="Who gave it"
          options={[
            { value: '', label: 'Not attributed to a named contact' },
            ...client.contacts.map((contact) => ({
              value: contact.id,
              label: contact.full_name,
            })),
          ]}
        />
        <FormInput
          form={form}
          name="policy_version"
          label="Policy version"
          hint="Which version of the policy they were shown. Optional, but it is what makes the decision meaningful later."
        />
        <FormCheckbox
          form={form}
          name="granted"
          label="They gave consent"
          hint="Leave unticked to record that consent was refused. A refusal is a decision and is worth recording."
        />
        <FormActions submitLabel="Record decision" busy={capture.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}

function WithdrawDialog({
  client,
  consent,
  onClose,
}: {
  client: ClientDetail;
  consent: ClientConsentRecord | null;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();

  const withdraw = useApiMutation<ClientConsentRecord, void>(
    {
      mutationFn: () =>
        api.post<ClientConsentRecord>(
          `${STAFF.clientConsentWithdraw(client.id, consent?.id ?? '')}/`,
        ),
      invalidates: [qk.sub('clients', client.id, 'consents')],
    },
    {
      onSuccess: () => {
        toast({
          title: 'Withdrawal recorded',
          description: 'The original decision is untouched. Both now appear in the history.',
          tone: 'success',
        });
        onClose();
      },
      onError: (error) => {
        toast({
          title: 'The withdrawal was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'The withdrawal could not be recorded. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <ConfirmModal
      open={consent !== null}
      onClose={onClose}
      title="Withdraw this consent"
      description={
        consent
          ? `${consent.consent_type}, given ${formatDateTime(consent.granted_at)}.`
          : ''
      }
      consequences="A NEW record is written saying consent was withdrawn today. The original stays exactly as it is — it is the proof that consent was validly held until now, and nothing in this system can alter it."
      confirmLabel="Record withdrawal"
      loading={withdraw.isPending}
      onConfirm={() => withdraw.mutate()}
    />
  );
}
